/* eslint-disable camelcase */
import { useEffect, useRef, useState } from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import styled from 'styled-components';
import { useIntl } from 'react-intl';
import { LogoHorizontal } from '../vectors/Logo';
import { Hamburger, IconButtonAnchor, IconButtonBtn } from '../ui/Buttons';
import { MailIcon, SearchIcon } from '../vectors/Icons';
import { easeInOutSine, hoverTiming } from '../styles/animations';
import { MobileMenu } from '../ui/MobileMenu';
import { AutoCompletePopup } from '../algolia/autocomplete/AutoCompletePopup';
import { LanguageSwitcher } from '../LanguageHelpers/LanguageSwitcher';
import { usePageLocale } from '../../hooks/usePageLocale';
import { useDefaultLocale } from '../../hooks/useDefaultLocale';

// Scoped styles

const Wrapper = styled.header`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--padding);
  background: var(--blue-2);
  z-index: 5;
  position: relative;
`;

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  width: ${({ fullWidth }) =>
    fullWidth ? '100%' : 'var(--layout-desktop-container)'};
  align-items: center;

  @media screen and (max-width: 1240px) {
    width: 100%;
  }

  @media screen and (max-width: 600px) {
    justify-content: space-between;
  }
`;

const MenuList = styled.ul`
  display: grid;
  grid-auto-flow: column;
  column-gap: var(--gap-3);
  list-style-type: none;
  align-items: center;

  @media screen and (max-width: 1200px) {
    column-gap: var(--gap-4);
  }

  @media screen and (max-width: 1120px) {
    display: none;
  }
`;

const MenuLink = styled(Link)`
  font-size: 0.965rem;
  text-transform: uppercase;
  color: var(--foreground-white);
  position: relative;

  &::after {
    opacity: 0;
    content: '';
    width: 100%;
    height: 3px;
    background: var(--gold-2);
    position: absolute;
    bottom: -4px;
    left: 0;
    transition: opacity ${hoverTiming} ${easeInOutSine};
  }

  @media (hover: hover) {
    &:hover {
      &::after {
        opacity: 1;
      }
    }
  }
`;

const IconsContainer = styled.div`
  display: grid;
  column-gap: var(--gap-6);
  grid-auto-flow: column;
  align-items: center;
  column-width: min-content;
`;

export const Header = ({ fullWidth }) => {
  const { locale } = usePageLocale();
  const intl = useIntl();
  const { defaultLocale } = useDefaultLocale();

  const [open, setOpen] = useState(null);
  const [autoComplete, setAutoComplete] = useState(null);
  const clickOutsideRef = useRef(null);
  const inputRef = useRef(null);

  const closePopup = (e) => {
    if (e.target === clickOutsideRef.current) {
      setAutoComplete(!autoComplete);
    }
  };

  useEffect(() => {
    if (autoComplete) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      document.addEventListener('click', closePopup);
      inputRef.current.focus();
    }
    return () => {
      document.body.style.overflow = 'auto';
      document.documentElement.style.overflow = 'auto';
      document.removeEventListener('click', closePopup);
      inputRef?.current?.blur();
    };
  }, [autoComplete]);

  const data = useStaticQuery(graphql`
    {
      allDatoCmsHeaderMenu {
        nodes {
          nodeLocale: locale
          vociDelMenu {
            id: originalId
            voce
            slug
            originalId
          }
        }
      }
    }
  `);

  const currentLocaleMenuNodes = data.allDatoCmsHeaderMenu.nodes.find(
    ({ nodeLocale }) => nodeLocale === locale
  );

  return (
    <>
      <Wrapper>
        <Container fullWidth={fullWidth}>
          <Link
            style={{ display: 'flex' }}
            aria-label="Logo"
            to={locale === defaultLocale ? '/' : `/${locale}`}
          >
            <LogoHorizontal />
          </Link>
          <MenuList>
            {currentLocaleMenuNodes.vociDelMenu.map(({ id, voce, slug }) => (
              <li key={id}>
                <MenuLink activeClassName="menuActive" to={`/${slug}`}>
                  {voce}
                </MenuLink>
              </li>
            ))}
          </MenuList>

          <IconsContainer>
            <IconButtonBtn
              onClick={() => setAutoComplete(!autoComplete)}
              ariaLabel={intl.formatMessage({ id: 'search' })}
              icon={<SearchIcon />}
            />
            <IconButtonAnchor
              className="mailIcon"
              iconLink="mailto:contattaci@larosadeigusti.com"
              aria-label={intl.formatMessage({ id: 'contact' })}
              icon={<MailIcon />}
            />
            <LanguageSwitcher />
            <Hamburger open={open} onClick={() => setOpen(!open)} />
          </IconsContainer>
        </Container>
      </Wrapper>
      {autoComplete === null ? null : (
        <AutoCompletePopup
          setAutoComplete={setAutoComplete}
          $autoComplete={autoComplete}
          clickOutsideRef={clickOutsideRef}
          inputRef={inputRef}
          closePopup={() => setAutoComplete(false)}
        />
      )}
      <MobileMenu $open={open} />
    </>
  );
};
